import React, { useEffect, useState, useMemo } from 'react'
import { unflatten } from 'flat'

import {
  CollectionPageProps,
  CollectionsSearchRequest,
  CollectionsValues,
} from './types'
import { Collection } from './'
import { query } from './algolia'
import { ShopifySectionComponent } from '@/types/ShopifySectionComponent'
import { useProductsQuery } from './Products/useProductsQuery'
import { BaseFilterValue, FilterBySection } from './Filters/types'
import { useTranslation } from 'react-i18next'
import { useOneTrust } from '@/hooks/useOneTrust'
import { useExponea } from '@/hooks/useExponea'
import { useSearchParams } from '@/hooks/useSearchParams'
import { useSearchParamsSync } from './useSearchParamsSync'
import { getWhitelistedTags } from './getWhitelistedProductFeatures/getWhitelistedProductFeatures'
import { getWhitelistedCategories } from './getWhitelistedCategories/getWhitelistedCategories'
import { algoliaDefaultValues } from '@/services/algolia'

const pageSize = 36

const facetMappings: Record<keyof CollectionsValues, string> = {
  band_size: 'filters.options.band_size',
  cup_size: 'filters.options.cup_size',
  size: 'filters.options.size',
  price_ratio: 'filters.price_ratio',
  color: 'filters.meta.lounge.canonical_color',
  tags: 'filters.meta.lounge.product_features',
  category: 'filters.meta.lounge.category',
  categories: 'filters.meta.lounge.categories',
}

const mapValuesToFacets = (values: CollectionsValues) =>
  unflatten<CollectionsSearchRequest, CollectionsSearchRequest>(
    Object.entries(values).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [facetMappings[key] ?? key]: value,
      }),
      {} as CollectionsSearchRequest,
    ),
  )

const CollectionPage: ShopifySectionComponent<CollectionPageProps> = ({
  data: {
    id = '',
    handle = '',
    promocards = [],
    isTitlePrimary = true,
    title = '',
    product_features_whitelist = [],
    enable_infinite_scroll = false,
    enable_swatches = false,
    hero_image,
    hero_image_desktop,
    hero_image_overlay,
    categories_whitelist = [],
  } = {},
}) => {
  const { searchParams } = useSearchParams()
  const { hasConsent } = useOneTrust()
  const { track } = useExponea()
  const { t } = useTranslation()
  const { defaultValues, getValues, updateSearchParams } =
    useSearchParamsSync<CollectionsValues>({
      defaultValues: algoliaDefaultValues,
    })

  const [filters, setFilters] = useState<CollectionsSearchRequest>(
    mapValuesToFacets(defaultValues),
  )

  const handleFiltersChanged = (values: CollectionsValues) => {
    updateSearchParams(values)
    setFilters(mapValuesToFacets(values))
  }

  // Keep search in-sync with browser back/forward navigation
  useEffect(() => setFilters(mapValuesToFacets(getValues())), [searchParams])

  const initialPageParam = searchParams.get('page')
    ? Number(searchParams.get('page'))
    : 1

  useEffect(() => {
    if (hasConsent('performance')) {
      track('view_category', {
        category_name: title,
        category_id: id,
      })
    }
  }, [])

  const {
    data: { pages = [] } = {},
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetching,
    isPending,
    isLoading,
  } = useProductsQuery({
    collectionId: handle,
    promoCards: promocards,
    useSwatches: enable_swatches ?? false,
    targetPageSize: pageSize,
    initialPageParam,
    queryFn: query,
    refetchOnWindowFocus: false,
    filterValues: filters,
  })

  const facets: { [key: string]: BaseFilterValue[] } =
    pages[pages.length - 1]?.availableFilters ?? {}

  const tags = useMemo(
    () =>
      getWhitelistedTags(
        product_features_whitelist,
        facets['meta.lounge.product_features'],
      ),
    [product_features_whitelist, facets['meta.lounge.product_features']],
  )

  const categories = useMemo(
    () =>
      getWhitelistedCategories(
        categories_whitelist,
        facets['meta.lounge.categories'],
      ),
    [categories_whitelist, facets['meta.lounge.categories']],
  )

  return (
    <>
      <Collection.HeroImage
        title={title}
        heroImage={hero_image}
        heroImageDesktop={hero_image_desktop}
        isTitlePrimary={isTitlePrimary}
        heroImageOverlay={hero_image_overlay}
      />
      <Collection>
        <Collection.Filters
          onSubmit={handleFiltersChanged}
          tagsInputName="tags"
          defaultValues={defaultValues}
          tags={tags}
          isLoading={isLoading}
          filterSections={
            [
              {
                title: t('general.discount'),
                filters: [
                  {
                    title: t('general.discount'),
                    titleHidden: true,
                    type: 'choicechips',
                    name: 'price_ratio',
                    values:
                      facets['price_ratio']
                        ?.filter((ratio) => {
                          const ratioValue = parseFloat(ratio.value)
                          return ratioValue > 0 && ratioValue < 1
                        })
                        ?.map(({ value, label }) => {
                          const discount = Math.round(
                            (1 - parseFloat(value)) * 100,
                          )
                          const roundedDiscount = discount - (discount % 5)
                          const displayLabel = `${roundedDiscount}%` ?? label
                          return { label: displayLabel, value }
                        })
                        ?.reduce<{ label: string; value: string }[]>(
                          (acc, { label, value }) => {
                            const existing = acc.find(
                              (item) => item.label === label,
                            )
                            if (existing) {
                              existing.value = `${existing.value},${value}`
                            } else {
                              acc.push({ label, value })
                            }
                            return acc
                          },
                          [],
                        )
                        .sort(
                          (a, b) => parseFloat(b.value) - parseFloat(a.value),
                        ) ?? [],
                  },
                ],
              },
              {
                title: t('general.category'),
                filters: [
                  {
                    title: 'category',
                    titleHidden: true,
                    type: 'checklist',
                    name: 'category',
                    values: facets['meta.lounge.category'] ?? [],
                  },
                ],
              },
              {
                title: t('general.categories'),
                filters: [
                  {
                    title: 'categories',
                    titleHidden: true,
                    type: 'checklist',
                    name: 'categories',
                    values: categories ?? [],
                  },
                ],
              },
              {
                title: t('general.color'),
                filters: [
                  {
                    title: t('general.color'),
                    titleHidden: true,
                    type: 'swatches',
                    name: 'color',
                    values: [
                      {
                        label: t('color.black'),
                        value: 'black',
                        color: '#000',
                      },
                      {
                        label: t('color.white'),
                        value: 'white',
                        color: '#EBE8EA',
                        checkColor: 'black',
                      },
                      {
                        label: t('color.cream'),
                        value: 'cream',
                        color: '#EAE6E3',
                        checkColor: 'black',
                      },
                      {
                        label: t('color.grey'),
                        value: 'grey',
                        color: '#C8C8C8',
                        checkColor: 'black',
                      },
                      {
                        label: t('color.brown'),
                        value: 'Brown',
                        color: '#88594E',
                      },
                      {
                        label: t('color.orange'),
                        value: 'orange',
                        color: '#F5BB6A',
                      },
                      {
                        label: t('color.purple'),
                        value: 'purple',
                        color: '#C285E0',
                      },
                      {
                        label: t('color.pink'),
                        value: 'pink',
                        color: '#EC6190',
                      },
                      { label: t('color.red'), value: 'red', color: '#B4321A' },
                      {
                        label: t('color.blue'),
                        value: 'blue',
                        color: '#2F74B7',
                      },
                      {
                        label: t('color.yellow'),
                        value: 'yellow',
                        color: '#F0E49C',
                        checkColor: 'black',
                      },
                      {
                        label: t('color.green'),
                        value: 'green',
                        color: '#205005',
                      },
                    ],
                  },
                ],
              },
              {
                title: t('general.bra_size'),
                filters: [
                  {
                    title: t('general.band_size'),
                    titleHidden: false,
                    type: 'choicechips',
                    name: 'band_size',
                    values: facets['options.band_size'] ?? [],
                  },
                  {
                    title: t('general.cup_size'),
                    titleHidden: false,
                    type: 'choicechips',
                    name: 'cup_size',
                    values: facets['options.cup_size'] ?? [],
                  },
                ],
              },
              {
                title: t('general.size'),
                filters: [
                  {
                    title: t('general.size'),
                    titleHidden: true,
                    type: 'checklist',
                    name: 'size',
                    values: facets['options.size'] ?? [],
                  },
                ],
              },
            ].filter((section) =>
              section.filters.some((filter) => filter.values.length > 0),
            ) as FilterBySection[]
          }
          sortOptions={[
            { label: t('general.most_relevant'), value: '' },
            { label: t('general.price_low_to_high'), value: 'price-asc' },
            { label: t('general.price_high_to_low'), value: 'price-desc' },
          ]}
        />
        <Collection.Products
          targetPageSize={pageSize}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          fetchNextPage={fetchNextPage}
          fetchPreviousPage={fetchPreviousPage}
          isInitialLoading={isPending}
          isFetchingNextPage={isFetchingNextPage}
          isFetching={isFetching}
          pages={pages}
          handle={handle ?? undefined}
          promoCards={promocards}
          enableInfiniteScroll={enable_infinite_scroll}
        />
      </Collection>
    </>
  )
}

export default CollectionPage
